"use client";

import Link from "next/link";
import { ArrowUpRight } from "lucide-react";
import { SectionHeader } from "./SectionHeader";
import { tools } from "@/config/tools";
import { Button } from "@/components/ui/button";
import { usePathname } from "next/navigation";
import { getLocaleFromPathname, withLocale } from "@/lib/i18n";
import { getSiteContent } from "@/lib/site-content";
import { getLocalizedTools } from "@/lib/tool-localization";

// Display only popular/featured tools (limit to 6)
const featuredTools = tools.filter(t => t.popular).slice(0, 6);
// Fallback if not enough popular tools
const displayedTools = featuredTools.length < 6 ? tools.slice(0, 6) : featuredTools;

// Helper to assign variants with a designed distribution to look random
const getVariant = (index: number) => {
  // Fixed pattern: Grey (Left) -> Green (Middle) -> Dark (Right)
  const pattern = ['grey', 'green', 'dark'];
  return pattern[index % pattern.length];
};

export function Tools() {
  const pathname = usePathname() || "/";
  const locale = getLocaleFromPathname(pathname);
  const content = getSiteContent(locale).landing;
  const localizedTools = getLocalizedTools(displayedTools, locale);

  return (
    <section id="tools" className="container mx-auto px-4 md:px-6 py-12">
      <SectionHeader 
        title={content.toolsTitle} 
        description={content.toolsDescription}
      />
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
        {localizedTools.map((tool, index) => {
           const variant = getVariant(index);
           
           return (
            <Link 
              href={withLocale(locale, `/${tool.slug}`)}
              key={tool.id}
              className="block"
            >
              <div 
                className={`
                  p-8 rounded-[45px] border border-positivus-dark flex flex-col h-[200px] relative overflow-hidden group transition-shadow hover:shadow-[0_5px_0_0_#191A23]
                  ${variant === 'grey' ? 'bg-positivus-grey' : 
                    variant === 'green' ? 'bg-positivus-green' : 'bg-positivus-dark'}
                `}
              >
                <div className="flex justify-between items-start z-10 w-full mb-4">
                   <h3 className={`text-xl font-medium px-3 py-1 rounded-[7px] inline-block w-fit
                     ${variant === 'grey' ? 'bg-black text-white' : ''}
                     ${variant === 'green' ? 'bg-white text-black' : ''}
                     ${variant === 'dark' ? 'bg-positivus-green text-black' : ''}
                   `}>
                     {tool.name}
                   </h3>
                   
                   <div className="flex items-center justify-center">
                      <div className={`
                        w-10 h-10 rounded-full flex items-center justify-center transition-transform group-hover:rotate-45
                        ${variant === 'dark' ? 'bg-white text-black' : 'bg-black text-positivus-green'}
                      `}>
                        <ArrowUpRight size={24} />
                      </div>
                   </div>
                </div>
                
                <div className="flex-1">
                   <span className={`text-base font-medium line-clamp-3
                      ${variant === 'dark' ? 'text-white/90' : 'text-black/80'}
                   `}>
                      {tool.description}
                   </span>
                </div>
              </div>
            </Link>
          );
        })}
      </div>

      <div className="flex justify-center">
        <Button asChild className="h-14 px-8 text-lg rounded-[14px] bg-positivus-dark text-white hover:bg-positivus-dark/90 hover:shadow-[0_5px_0_0_#191A23] hover:-translate-y-1 transition-all">
          <Link href={withLocale(locale, "/tools")}>{content.toolsCta}</Link>
        </Button>
      </div>
    </section>
  );
}
